<template>
  <div class="permission-groups">
    <div class="permission-groups__header">
      <div class="header-left">
        <BaseButton variant="outline" @click="router.push('/main/admin/users')">
          返回用户管理
        </BaseButton>
        <BaseButton variant="primary" @click="handleAddGroup">
          <el-icon><Plus /></el-icon>新增权限组
        </BaseButton>
      </div>
    </div>
    
    <div class="permission-groups__list">
      <BaseCard
        v-for="group in permissionGroups"
        :key="group._id"
        class="permission-group-card"
      >
        <div class="permission-group-card__header">
          <div class="permission-group-card__name" @click="startEditGroupName(group)" v-if="!group.isEditing">
            {{ group.name }}
          </div>
          <BaseInput
            v-else
            v-model="group.editingName"
            size="small"
            @blur="saveGroupName(group)"
            @keyup.enter="saveGroupName(group)"
            style="width: 200px"
          />
          <div class="permission-group-card__actions">
            <BaseButton variant="text" size="small" @click="handleEditGroup(group)">
              <el-icon><Edit /></el-icon>
            </BaseButton>
            <BaseButton variant="danger" size="small" @click="handleDeleteGroup(group)">
              <el-icon><Delete /></el-icon>
            </BaseButton>
          </div>
        </div>
        <div class="permission-group-card__description">
          {{ group.description || '暂无描述' }}
        </div>
        <div class="permission-group-card__role">
          对应身份：{{ roleLabel(group.defaultRole) }}
        </div>
      </BaseCard>
    </div>

    <!-- 权限组对话框 -->
    <el-dialog
      v-model="addGroupDialog.visible"
      :title="addGroupDialog.isEdit ? '编辑权限组' : '新增权限组'"
      width="800px"
      :destroy-on-close="true"
      :close-on-click-modal="false"
      append-to-body
    >
      <el-form
        ref="addGroupFormRef"
        :model="addGroupDialog.form"
        :rules="addGroupDialog.rules"
        label-width="100px"
      >
        <el-form-item label="名称" prop="name">
          <BaseInput v-model="addGroupDialog.form.name" placeholder="请输入权限组名称" />
        </el-form-item>

        <el-form-item label="描述" prop="description">
          <BaseInput
            v-model="addGroupDialog.form.description"
            type="textarea"
            :rows="2"
            placeholder="请输入权限组描述"
          />
        </el-form-item>

        <el-form-item label="对应身份" prop="defaultRole">
          <el-select v-model="addGroupDialog.form.defaultRole" placeholder="请选择对应身份" style="width: 100%">
            <el-option
              v-for="opt in roleOptions"
              :key="opt.value"
              :label="opt.label"
              :value="opt.value"
            />
          </el-select>
          <div class="form-hint">用户加入此权限组后，身份会按此设置（影响订单经办人等按身份筛选的功能）。</div>
        </el-form-item>

        <template v-if="permissionFields">
          <div class="permissions-container">
            <div class="permissions-list">
              <div
                v-for="(moduleConfig, moduleName) in permissionFields"
                :key="moduleName"
                class="permission-module"
              >
                <div class="module-header" @click="toggleModule(moduleName)">
                  <el-checkbox
                    :model-value="isModuleAllSelected(moduleName)"
                    :indeterminate="isModuleIndeterminate(moduleName)"
                    @change="(val) => handleModuleSelectAll(moduleName, val)"
                    @click.stop
                  >
                    <span class="module-name">{{ moduleConfig.name }}</span>
                  </el-checkbox>
                  <el-icon class="module-chevron" :class="{ expanded: expandedModules[moduleName] }">
                    <ArrowDown />
                  </el-icon>
                </div>
                <div v-show="expandedModules[moduleName]" class="module-body">
                  <div
                    v-for="item in flattenModulePermissions(moduleConfig)"
                    :key="item.value"
                    class="permission-item"
                  >
                    <el-checkbox
                      :model-value="addGroupDialog.form.permissions[moduleName]?.includes(item.value)"
                      @change="(val) => handlePermissionChange(moduleName, item.value, val)"
                    >
                      {{ item.name }}
                    </el-checkbox>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </template>
      </el-form>

      <template #footer>
        <BaseButton variant="outline" @click="addGroupDialog.visible = false">取消</BaseButton>
        <BaseButton
          variant="primary"
          @click="handleSaveAddGroup" 
          :loading="submitting"
        >
          确定
        </BaseButton>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { ElMessageBox, ElMessage } from 'element-plus'
import { Plus, Edit, Delete, ArrowDown } from '@element-plus/icons-vue'
import BaseButton from '@/components/base/BaseButton.vue'
import BaseInput from '@/components/base/BaseInput.vue'
import BaseCard from '@/components/base/BaseCard.vue'
import {
  createPermissionGroup,
  getPermissionGroups,
  getPermissionGroup,
  updatePermissionGroup,
  deletePermissionGroup,
  getPermissionFields
} from '@/api/permission'
import {
  findMissingDepsFor,
  findAllMissingDeps,
  describePermission
} from '@/utils/permissionDependencies'

const router = useRouter()
const addGroupFormRef = ref(null)
const submitting = ref(false)

// 权限组列表
const permissionGroups = ref([])
const permissionFields = ref(null)
const expandedModules = ref({})

// 权限组对应身份（用户 role 由此派生）
const roleOptions = [
  { value: 'user', label: '普通用户' },
  { value: 'teacher', label: '教师' },
  { value: 'academic', label: '教务' },
  { value: 'admin', label: '管理员' }
]

const roleLabel = (value) => {
  const found = roleOptions.find(opt => opt.value === value)
  return found ? found.label : '普通用户'
}

const flattenModulePermissions = (moduleConfig) => {
  return Object.values(moduleConfig.options).flatMap(option => option.values)
}

const isModuleAllSelected = (moduleName) => {
  const moduleConfig = permissionFields.value?.[moduleName]
  if (!moduleConfig) return false
  const allValues = flattenModulePermissions(moduleConfig).map(item => item.value)
  const selected = addGroupDialog.value.form.permissions[moduleName] || []
  return allValues.length > 0 && allValues.every(val => selected.includes(val))
}

const isModuleIndeterminate = (moduleName) => {
  const moduleConfig = permissionFields.value?.[moduleName]
  if (!moduleConfig) return false
  const allValues = flattenModulePermissions(moduleConfig).map(item => item.value)
  const selected = addGroupDialog.value.form.permissions[moduleName] || []
  return selected.length > 0 && selected.length < allValues.length
}

// 把一组依赖加入到当前表单（已存在的会跳过）
const applyDeps = (deps) => {
  deps.forEach((dep) => {
    const list = addGroupDialog.value.form.permissions[dep.module] || []
    if (list.includes('*') || list.includes(dep.action)) return
    addGroupDialog.value.form.permissions[dep.module] = [...list, dep.action]
  })
}

// 勾选某项后，检查它的隐式依赖；若有未勾的，弹出确认让用户选择一并启用。
const promptForMissingDeps = async (moduleName, action) => {
  const missing = findMissingDepsFor(
    addGroupDialog.value.form.permissions,
    moduleName,
    action
  )
  if (missing.length === 0) return
  const lines = missing
    .map((d) => `• ${describePermission(permissionFields.value, d.module, d.action)}（${d.reason}）`)
    .join('<br/>')
  try {
    await ElMessageBox.confirm(
      `启用此项还建议同时启用以下关联权限，否则相关页面可能弹出"没有权限访问"：<br/><br/>${lines}`,
      '关联权限提示',
      {
        confirmButtonText: '一并启用',
        cancelButtonText: '仅启用当前项',
        type: 'warning',
        dangerouslyUseHTMLString: true
      }
    )
    applyDeps(missing)
  } catch {
    /* 用户选择仅启用当前项，不做处理 */
  }
}

const handleModuleSelectAll = async (moduleName, checked) => {
  if (!addGroupDialog.value.form.permissions[moduleName]) {
    addGroupDialog.value.form.permissions[moduleName] = []
  }
  if (checked) {
    const moduleConfig = permissionFields.value?.[moduleName]
    if (!moduleConfig) return
    const allValues = flattenModulePermissions(moduleConfig).map(item => item.value)
    addGroupDialog.value.form.permissions[moduleName] = allValues
    // 汇总该模块下所有项的依赖，一次性提示
    const seen = new Set()
    const missing = []
    allValues.forEach((action) => {
      findMissingDepsFor(addGroupDialog.value.form.permissions, moduleName, action).forEach((d) => {
        const k = `${d.module}.${d.action}`
        if (!seen.has(k)) {
          seen.add(k)
          missing.push(d)
        }
      })
    })
    if (missing.length > 0) {
      const lines = missing
        .map((d) => `• ${describePermission(permissionFields.value, d.module, d.action)}（${d.reason}）`)
        .join('<br/>')
      try {
        await ElMessageBox.confirm(
          `本模块下有项依赖以下关联权限：<br/><br/>${lines}`,
          '关联权限提示',
          {
            confirmButtonText: '一并启用',
            cancelButtonText: '仅启用本模块',
            type: 'warning',
            dangerouslyUseHTMLString: true
          }
        )
        applyDeps(missing)
      } catch {
        /* ignore */
      }
    }
  } else {
    addGroupDialog.value.form.permissions[moduleName] = []
  }
}

const handlePermissionChange = async (moduleName, value, checked) => {
  if (!addGroupDialog.value.form.permissions[moduleName]) {
    addGroupDialog.value.form.permissions[moduleName] = []
  }
  if (checked) {
    addGroupDialog.value.form.permissions[moduleName].push(value)
    await promptForMissingDeps(moduleName, value)
  } else {
    addGroupDialog.value.form.permissions[moduleName] = addGroupDialog.value.form.permissions[moduleName].filter(v => v !== value)
  }
}

const toggleModule = (moduleName) => {
  expandedModules.value[moduleName] = !expandedModules.value[moduleName]
}

const getModuleValidValues = (moduleName) => {
  if (!permissionFields.value?.[moduleName]) return []
  return Object.values(permissionFields.value[moduleName].options)
    .flatMap(option => option.values.map(item => item.value))
}

// 加载权限字段配置
const loadPermissionFields = async () => {
  try {
    const response = await getPermissionFields()
    if (response.status === 'success') {
      permissionFields.value = response.data.permissionFields
      const modules = Object.keys(permissionFields.value)
      modules.forEach((key, i) => {
        expandedModules.value[key] = i === 0
      })
    }
  } catch (error) {
    console.error('加载权限字段失败:', error)
    ElMessage.error('加载权限字段失败')
  }
}

// 加载权限组列表
const loadPermissionGroups = async () => {
  try {
    const res = await getPermissionGroups()
    console.log('res',res)
    if (res.status === 'success') {
      // 只显示状态为active的权限组
      permissionGroups.value = res.data.permissionGroups
        .filter(group => group.status === 'active')
        .map(group => ({
          ...group,
          isEditing: false,
          editingName: ''
        }))
    }
  } catch (error) {
    console.error('加载权限组列表失败:', error)
    ElMessage.error('加载权限组列表失败')
  }
}

// 新建权限组对话框
const addGroupDialog = ref({
  visible: false,
  saving: false,
  isEdit: false,
  form: {
    _id: null,
    name: '',
    description: '',
    defaultRole: 'user',
    permissions: {}
  },
  rules: {
    name: [
      { required: true, message: '请输入权限组名称', trigger: 'blur' },
      { min: 2, max: 20, message: '长度在 2 到 20 个字符', trigger: 'blur' }
    ],
    description: [
      { max: 100, message: '长度不能超过 100 个字符', trigger: 'blur' }
    ]
  }
})

// 处理添加权限组
const handleAddGroup = () => {
  addGroupDialog.value.visible = true
  addGroupDialog.value.isEdit = false
  addGroupDialog.value.form.name = ''
  addGroupDialog.value.form.description = ''
  addGroupDialog.value.form.defaultRole = 'user'
  addGroupDialog.value.form._id = null
  addGroupDialog.value.form.permissions = {}
  if (permissionFields.value) {
    const modules = Object.keys(permissionFields.value)
    modules.forEach(key => { expandedModules.value[key] = false })
    if (modules.length > 0) expandedModules.value[modules[0]] = true
  }
}

// 开始编辑权限组名称
const startEditGroupName = (group) => {
  if (group.name === 'superadmin') return
  group.isEditing = true
  group.editingName = group.name
}

// 处理更新权限组
const handleUpdateGroup = async (groupId, updateData) => {
  try {
    submitting.value = true
    console.log('更新权限组数据:', updateData)

    // 支持同时更新名称、描述、权限
    const data = {}
    if (typeof updateData.name === 'string') data.name = updateData.name
    if (typeof updateData.description === 'string') data.description = updateData.description
    if (typeof updateData.defaultRole === 'string') data.defaultRole = updateData.defaultRole
    if (updateData.permissions) data.permissions = updateData.permissions
    
    const res = await updatePermissionGroup(groupId, data)
    
    if (res.status === 'success') {
      ElMessage.success('更新权限组成功')
      loadPermissionGroups()
      return true
    } else {
      throw new Error(res.message || '更新失败')
    }
  } catch (error) {
    console.error('更新权限组失败:', error)
    console.error('错误响应:', error.response?.data)
    ElMessage.error(error.response?.data?.message || '更新失败')
    return false
  } finally {
    submitting.value = false
  }
}

// 修改 saveGroupName 函数
const saveGroupName = async (group) => {
  if (!group.editingName.trim()) {
    group.editingName = group.name
    group.isEditing = false
    return
  }
  
  if (group.editingName.trim() === group.name) {
    group.isEditing = false
    return
  }
  
  const success = await handleUpdateGroup(group._id, {
    name: group.editingName.trim()
  })
  
  if (success) {
    group.name = group.editingName.trim()
  } else {
    group.editingName = group.name
  }
  group.isEditing = false
}

// 处理编辑权限组
const handleEditGroup = async (group) => {
  try {
    // 获取权限组详情
    const res = await getPermissionGroup(group._id)
    if (res.status === 'success') {
      const groupPermissions = res.data.permissionGroup.permissions
      
      // 设置基本信息
      addGroupDialog.value.form.name = group.name
      addGroupDialog.value.form.description = group.description
      addGroupDialog.value.form.defaultRole = res.data.permissionGroup.defaultRole || 'user'
      addGroupDialog.value.form._id = group._id
      
      // 重置权限
      addGroupDialog.value.form.permissions = {}
      
      // 处理权限,如果有通配符则选中该模块所有权限
      Object.keys(groupPermissions).forEach(moduleName => {
        addGroupDialog.value.form.permissions[moduleName] = []
        
        if (groupPermissions[moduleName].includes('*')) {
          // 如果有通配符,选中该模块下所有权限
          if (permissionFields.value[moduleName]) {
            Object.values(permissionFields.value[moduleName].options).forEach(option => {
              addGroupDialog.value.form.permissions[moduleName].push(
                ...option.values.map(item => item.value)
              )
            })
          }
        } else {
          // 否则只选中指定的权限
          const validValues = new Set(getModuleValidValues(moduleName))
          addGroupDialog.value.form.permissions[moduleName] = (groupPermissions[moduleName] || [])
            .filter(value => validValues.has(value))
        }
      })
      
      // 显示对话框
      addGroupDialog.value.visible = true
      addGroupDialog.value.isEdit = true
      
      if (permissionFields.value) {
        const modules = Object.keys(permissionFields.value)
        modules.forEach(key => { expandedModules.value[key] = false })
        if (modules.length > 0) expandedModules.value[modules[0]] = true
      }
    }
  } catch (error) {
    console.error('获取权限组详情失败:', error)
    ElMessage.error('获取权限组详情失败')
  }
}

// 处理删除权限组
const handleDeleteGroup = (group) => {
  if (group.name === 'superadmin') {
    ElMessage.warning('超级管理员权限组不能删除')
    return
  }
  
  ElMessageBox.confirm(
    `确定要删除权限组"${group.name}"吗？`,
    '提示',
    {
      confirmButtonText: '确定',
      cancelButtonText: '取消',
      type: 'warning'
    }
  ).then(async () => {
    try {
      const res = await deletePermissionGroup(group._id)
      if (res.status === 'success') {
        ElMessage.success('删除成功')
        loadPermissionGroups() // 重新加载权限组列表
      } else {
        throw new Error(res.message || '删除失败')
      }
    } catch (error) {
      console.error('删除权限组失败:', error)
      ElMessage.error(error.response?.data?.message || '删除失败')
    }
  }).catch(() => {})
}

// 保存权限组
const handleSaveAddGroup = async () => {
  if (!addGroupFormRef.value) return

  try {
    await addGroupFormRef.value.validate()

    // 保存前总扫描：找出整组里未满足的关联权限
    const missing = findAllMissingDeps(addGroupDialog.value.form.permissions)
    if (missing.length > 0) {
      const lines = missing
        .map((d) => {
          const trig = describePermission(permissionFields.value, d.triggeredBy.module, d.triggeredBy.action)
          const dep = describePermission(permissionFields.value, d.module, d.action)
          return `• ${trig} 需要 ${dep}（${d.reason}）`
        })
        .join('<br/>')
      try {
        await ElMessageBox.confirm(
          `检测到以下关联权限尚未启用：<br/><br/>${lines}<br/><br/>是否一并启用后再保存？`,
          '关联权限检查',
          {
            confirmButtonText: '一并启用并保存',
            cancelButtonText: '忽略并保存',
            type: 'warning',
            distinguishCancelAndClose: true,
            dangerouslyUseHTMLString: true
          }
        )
        applyDeps(missing)
      } catch (action) {
        if (action === 'close') return // 关闭对话框，取消保存
        // 'cancel' = 忽略并保存，继续往下走
      }
    }

    addGroupDialog.value.saving = true

    // 处理权限数据,检查是否可以使用通配符
    const permissions = {}
    Object.keys(addGroupDialog.value.form.permissions).forEach(moduleName => {
      const selectedPermissions = addGroupDialog.value.form.permissions[moduleName]
      if (!selectedPermissions || selectedPermissions.length === 0) return
      
      // 计算该模块下所有可用权限
      const allPermissions = []
      if (permissionFields.value[moduleName]) {
        Object.values(permissionFields.value[moduleName].options).forEach(option => {
          allPermissions.push(...option.values.map(item => item.value))
        })
      }
      
      // 如果选中了所有权限,使用通配符
      if (selectedPermissions.length === allPermissions.length) {
        permissions[moduleName] = ['*']
      } else {
        permissions[moduleName] = selectedPermissions
      }
    })
    
    if (addGroupDialog.value.isEdit) {
      // 编辑模式下同时支持更新名称/描述/权限
      const success = await handleUpdateGroup(addGroupDialog.value.form._id, {
        name: addGroupDialog.value.form.name.trim(),
        description: addGroupDialog.value.form.description.trim(),
        defaultRole: addGroupDialog.value.form.defaultRole,
        permissions
      })
      if (success) {
        addGroupDialog.value.visible = false
      }
    } else {
      // 创建模式下发送完整数据
      const data = {
        name: addGroupDialog.value.form.name.trim(),
        description: addGroupDialog.value.form.description.trim() || '权限组',
        defaultRole: addGroupDialog.value.form.defaultRole,
        permissions,
        type: 'custom'
      }
      
      console.log('创建权限组数据:', data)
      
      const res = await createPermissionGroup(data)
      if (res.status === 'success') {
        ElMessage.success('创建权限组成功')
        addGroupDialog.value.visible = false
        loadPermissionGroups()
      }
    }
  } catch (error) {
    console.error(addGroupDialog.value.isEdit ? '更新权限组失败:' : '创建权限组失败:', error)
    console.error('错误响应:', error.response?.data)
    ElMessage.error(error.response?.data?.message || (addGroupDialog.value.isEdit ? '更新权限组失败' : '创建权限组失败'))
  } finally {
    addGroupDialog.value.saving = false
  }
}

onMounted(() => {
  loadPermissionGroups()
  loadPermissionFields()
})
</script>

<style lang="scss" scoped>
.permission-groups {
  height: 100%;
  padding: var(--space-5);
  background: var(--color-bg);

  &__header {
    margin-bottom: var(--space-4);

    .header-left {
      display: flex;
      gap: var(--space-3);
      align-items: center;
    }
  }

  &__list {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
    gap: var(--space-4);
  }
}

.permission-group-card {
  :deep(.base-card__body) {
    padding-top: 0;
  }

  &__header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: var(--space-2);
  }

  &__name {
    font-size: var(--font-size-body-lg);
    font-weight: 600;
    cursor: pointer;
    color: var(--color-text-primary);

    &:hover {
      color: var(--color-primary);
    }
  }

  &__actions {
    display: flex;
    gap: var(--space-2);
  }

  &__description {
    color: var(--color-text-secondary);
    font-size: var(--font-size-body-sm);
  }

  &__role {
    margin-top: var(--space-2);
    color: var(--color-text-secondary);
    font-size: var(--font-size-body-sm);
  }
}

.form-hint {
  margin-top: var(--space-1);
  color: var(--color-text-secondary);
  font-size: var(--font-size-body-sm);
  line-height: 1.4;
}

.permissions-container {
  max-height: 60vh;
  overflow-y: auto;
  padding: 0 var(--space-4);
  margin: 0 calc(var(--space-4) * -1);
  
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-thumb {
    background-color: var(--el-border-color-lighter);
    border-radius: 3px;
  }
  
  &::-webkit-scrollbar-track {
    background-color: transparent;
  }
}

.permissions-list {
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
}

.permission-module {
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  overflow: hidden;
  background: var(--color-surface);
}

.module-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: var(--space-3) var(--space-4);
  cursor: pointer;
  user-select: none;
  transition: background-color var(--motion-fast) var(--ease-standard);

  &:hover {
    background-color: var(--color-surface-muted);
  }

  .module-name {
    font-weight: 600;
    font-size: var(--font-size-body);
    color: var(--color-text-primary);
  }

  .module-chevron {
    font-size: 16px;
    color: var(--color-text-secondary);
    transition: transform var(--motion-fast) var(--ease-standard);

    &.expanded {
      transform: rotate(180deg);
    }
  }
}

.module-body {
  border-top: 1px solid var(--color-border);
  background: var(--color-bg);
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 0;
}

.permission-item {
  padding: var(--space-2) var(--space-4);
  transition: background-color var(--motion-fast) var(--ease-standard);

  &:hover {
    background-color: var(--color-surface-muted);
  }

  :deep(.el-checkbox__label) {
    font-size: var(--font-size-body-sm);
  }
}

::deep(.el-dialog) {
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-md);
}

::deep(.el-dialog__header) {
  margin: 0;
  padding: var(--space-4);
  background: var(--color-surface-muted);
  border-bottom: 1px solid var(--color-border);
}

::deep(.el-dialog__title) {
  color: var(--color-text-primary);
  font-size: var(--font-size-h3);
  font-weight: 600;
}

::deep(.el-dialog__body),
::deep(.el-dialog__footer) {
  padding: var(--space-4);
}

::deep(.el-dialog__footer) {
  border-top: 1px solid var(--color-border);
}
</style> 